import React, { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, CheckCircle, Award } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COURSES = [
  "PMP® Masterclass",
  "PMP® Exam Prep Seminar",
  "PMP® Exam Cram",
  "CAPM® Exam Prep Seminar",
  "CAPM® Exam Cram",
  "PMI-ACP® Exam Prep Seminar",
];

const Reissue = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [certId, setCertId] = useState("");
  const [email, setEmail] = useState("");
  
  const [formData, setFormData] = useState({
    student_name: "",
    course: "",
    completion_date: "",
    email: "",
    attested: false,
  });

  const isFormValid = () => {
    return (
      formData.student_name.trim() !== "" &&
      formData.course !== "" &&
      formData.completion_date !== "" &&
      formData.email.trim() !== "" &&
      formData.attested === true
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isFormValid()) {
      toast.error("Please fill in all fields and check the attestation box");
      return;
    }

    // Validate date is not in future
    const completionDate = new Date(formData.completion_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (completionDate > today) {
      toast.error("Completion date cannot be in the future");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/reissue`, formData);
      
      if (response.data.success) {
        setCertId(response.data.cert_id);
        setEmail(formData.email);
        setSuccess(true);
        toast.success("Certificate sent successfully!");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || "Failed to generate certificate";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center" data-testid="success-card">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Certificate Sent!
          </h1>
          <p className="text-slate-600 mb-6">
            Your certificate (<span className="font-semibold text-slate-900">{certId}</span>) has been emailed to{" "}
            <span className="font-semibold text-slate-900">{email}</span>.
          </p>
          <p className="text-sm text-slate-500">
            Please check your inbox — it may take a few minutes to arrive.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 py-4">
        <div className="max-w-2xl mx-auto px-6 flex items-center gap-3">
          <Award className="h-8 w-8 text-teal-600" />
          <span className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Instructing.com
          </span>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-medium text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Request Your Certificate
          </h1>
          <p className="text-slate-600">
            Complete the form below to receive your certificate by email.
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-8" data-testid="reissue-form-card">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Full Name */}
            <div>
              <Label htmlFor="student_name" className="text-sm font-medium text-slate-700 mb-1.5 block">
                Full Name
              </Label>
              <Input
                id="student_name"
                type="text"
                value={formData.student_name}
                onChange={(e) => setFormData({ ...formData, student_name: e.target.value })}
                placeholder="Enter your name exactly as you want it on the certificate"
                maxLength={100}
                className="h-11 rounded-lg border-slate-200"
                data-testid="student-name-input"
              />
            </div>

            {/* Course Completed */}
            <div>
              <Label htmlFor="course" className="text-sm font-medium text-slate-700 mb-1.5 block">
                Course Completed
              </Label>
              <Select
                value={formData.course}
                onValueChange={(value) => setFormData({ ...formData, course: value })}
              >
                <SelectTrigger className="h-11 rounded-lg border-slate-200" data-testid="course-select">
                  <SelectValue placeholder="Select your course" />
                </SelectTrigger>
                <SelectContent>
                  {COURSES.map((course) => (
                    <SelectItem key={course} value={course} data-testid={`course-option-${course.replace(/[^a-zA-Z]/g, '-')}`}>
                      {course}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date of Completion */}
            <div>
              <Label htmlFor="completion_date" className="text-sm font-medium text-slate-700 mb-1.5 block">
                Date of Completion
              </Label>
              <Input
                id="completion_date"
                type="date"
                value={formData.completion_date}
                onChange={(e) => setFormData({ ...formData, completion_date: e.target.value })}
                max={new Date().toISOString().split('T')[0]}
                className="h-11 rounded-lg border-slate-200"
                data-testid="completion-date-input"
              />
            </div>

            {/* Email Address */}
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700 mb-1.5 block">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="your@email.com"
                className="h-11 rounded-lg border-slate-200"
                data-testid="email-input"
              />
            </div>

            {/* Attestation Checkbox */}
            <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <Checkbox
                id="attested"
                checked={formData.attested}
                onCheckedChange={(checked) => setFormData({ ...formData, attested: checked })}
                className="mt-0.5"
                data-testid="attestation-checkbox"
              />
              <label
                htmlFor="attested"
                className="text-sm text-slate-600 leading-relaxed cursor-pointer"
              >
                I confirm that I personally completed this course and the information provided is accurate.
              </label>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={!isFormValid() || loading}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-lg py-3 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Generating Certificate...
                </>
              ) : (
                "Request Certificate"
              )}
            </Button>
          </form>
        </div>

        {/* Help Text */}
        <p className="text-center text-sm text-slate-500 mt-6">
          Having trouble? Contact us at{" "}
          <a href="mailto:cs@instructing.com" className="text-teal-600 hover:underline">
            cs@instructing.com
          </a>
        </p>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-6 mt-12">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <p className="text-sm text-slate-500">© 2025 Instructing.com, LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Reissue;
